'use client';

import React, { useEffect, useState } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  ShareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { confirm } from '@/lib/notifications';
import type { ClarifyType } from '@/lib/chat/clarify';

interface StoredClarificationEntry {
  text: string;
  placeholderCount: number;
  skipped?: boolean;
  updatedAt: number;
}

interface ClarificationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  clarifications: Partial<Record<ClarifyType, StoredClarificationEntry | undefined>>;
  onClearType: (type: ClarifyType) => Promise<void> | void;
  onClearAll: () => Promise<void> | void;
}

const CLARIFICATION_ORDER: ClarifyType[] = ['press', 'pitch', 'social', 'research', 'generic'];

const CLARIFICATION_TYPE_CONFIG: Record<ClarifyType, { icon: React.ElementType; label: string; description: string; color: string }> = {
  press: {
    icon: DocumentTextIcon,
    label: 'Press Release',
    description: 'Press release details and requirements',
    color: 'blue',
  },
  pitch: {
    icon: PaperAirplaneIcon,
    label: 'Media Pitch',
    description: 'Pitch context and journalist information',
    color: 'purple',
  },
  social: {
    icon: ShareIcon,
    label: 'Social Media',
    description: 'Social platform and audience details',
    color: 'green',
  },
  research: {
    icon: MagnifyingGlassIcon,
    label: 'Research',
    description: 'Research requirements and scope',
    color: 'orange',
  },
  generic: {
    icon: ExclamationTriangleIcon,
    label: 'Additional Context',
    description: 'General clarifications',
    color: 'gray',
  },
};

export function ClarificationsDrawer({
  isOpen,
  onClose,
  conversationId,
  clarifications,
  onClearType,
  onClearAll,
}: ClarificationsDrawerProps) {
  const [expandedTypes, setExpandedTypes] = useState<Record<ClarifyType, boolean>>({});
  const [clearingType, setClearingType] = useState<ClarifyType | null>(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [isVisible, setIsVisible] = useState(isOpen);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setAnimateIn(false);
      const frame = requestAnimationFrame(() => setAnimateIn(true));
      return () => cancelAnimationFrame(frame);
    }
    if (!isOpen && isVisible) {
      setAnimateIn(false);
      const timeout = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isOpen, isVisible]);

  if (!isVisible) return null;

  const clarificationEntries = CLARIFICATION_ORDER
    .map((type) => [type, clarifications[type]] as [ClarifyType, StoredClarificationEntry | undefined])
    .filter(([, value]) => value !== undefined) as [ClarifyType, StoredClarificationEntry][];

  const hasAnyClarifications = clarificationEntries.length > 0;

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string; iconBg: string }> = {
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-950/30',
        border: 'border-blue-200 dark:border-blue-800',
        text: 'text-blue-900 dark:text-blue-100',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50',
      },
      purple: {
        bg: 'bg-purple-50 dark:bg-purple-950/30',
        border: 'border-purple-200 dark:border-purple-800',
        text: 'text-purple-900 dark:text-purple-100',
        iconBg: 'bg-purple-100 dark:bg-purple-900/50',
      },
      green: {
        bg: 'bg-green-50 dark:bg-green-950/30',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-900 dark:text-green-100',
        iconBg: 'bg-green-100 dark:bg-green-900/50',
      },
      orange: {
        bg: 'bg-orange-50 dark:bg-orange-950/30',
        border: 'border-orange-200 dark:border-orange-800',
        text: 'text-orange-900 dark:text-orange-100',
        iconBg: 'bg-orange-100 dark:bg-orange-900/50',
      },
      gray: {
        bg: 'bg-muted/50 dark:bg-gray-950/30',
        border: 'border-border dark:border-gray-800',
        text: 'text-foreground dark:text-gray-100',
        iconBg: 'bg-muted dark:bg-muted/50',
      },
    };
    return colors[color] || colors.gray;
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  const handleClearType = async (type: ClarifyType, label: string) => {
    if (clearingAll || clearingType) return;
    const shouldClear = await confirm({
      title: 'Clear clarification',
      message: `Clear ${label} clarification?`,
      confirmText: 'Clear',
      cancelText: 'Cancel',
      type: 'warning',
    });
    if (shouldClear) {
      setClearingType(type);
      try {
        await onClearType(type);
      } finally {
        setClearingType(null);
      }
    }
  };

  const handleClearAll = async () => {
    if (clearingAll || clearingType) return;
    const shouldClear = await confirm({
      title: 'Clear clarifications',
      message: 'Clear all clarifications for this conversation?',
      confirmText: 'Clear all',
      cancelText: 'Cancel',
      type: 'warning',
    });
    if (shouldClear) {
      setClearingAll(true);
      try {
        await onClearAll();
        onClose();
      } finally {
        setClearingAll(false);
      }
    }
  };

  const isInteractive = isOpen && !clearingAll;

  return (
    <>
      <div
        className={[
          'fixed inset-0 bg-black/50 z-40 backdrop-blur-sm transition-opacity duration-200',
          animateIn ? 'opacity-100' : 'opacity-0',
          isInteractive ? 'pointer-events-auto' : 'pointer-events-none'
        ].join(' ')}
        onClick={() => {
          if (isInteractive) onClose();
        }}
      />

      <div
        className={[
          'fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-muted shadow-2xl z-50 flex flex-col',
          'transition-transform duration-200 ease-out',
          animateIn ? 'translate-x-0' : 'translate-x-full',
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="Saved clarifications"
        data-conversation-id={conversationId}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border dark:border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-foreground dark:text-white">
              Saved Clarifications
            </h2>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">
              {hasAnyClarifications
                ? `${clarificationEntries.length} type${clarificationEntries.length !== 1 ? 's' : ''} saved`
                : 'No clarifications saved'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted dark:hover:bg-gray-800 transition-colors"
            aria-label="Close drawer"
          >
            <XMarkIcon className="w-5 h-5 text-muted-foreground dark:text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!hasAnyClarifications ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-muted-foreground dark:text-foreground mb-3" />
              <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                No clarifications saved yet
              </p>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">
                Clarifications are saved automatically as you provide them
              </p>
            </div>
          ) : (
            <>
              {clarificationEntries.map(([type, entry]) => {
                const config = CLARIFICATION_TYPE_CONFIG[type];
                const Icon = config.icon;
                const colors = getColorClasses(config.color);
                const isExpanded = !!expandedTypes[type];
                const shouldTruncate = entry.text.length > 150;
                const displayText = shouldTruncate && !isExpanded
                  ? `${entry.text.slice(0, 150).trim()}...`
                  : entry.text;

                return (
                  <div
                    key={type}
                    className={`rounded-lg border ${colors.border} ${colors.bg} p-4`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`${colors.iconBg} rounded-lg p-2 flex-shrink-0`}>
                          <Icon className={`w-4 h-4 ${colors.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-semibold ${colors.text}`}>
                            {config.label}
                          </h3>
                          <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">
                            {config.description}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleClearType(type, config.label)}
                        className={[
                          'p-2 rounded transition-colors group',
                          clearingAll || clearingType === type
                            ? 'opacity-60 cursor-not-allowed'
                            : 'hover:bg-red-100 dark:hover:bg-red-900/30'
                        ].join(' ')}
                        aria-label={`Clear ${config.label}`}
                        disabled={clearingAll || clearingType === type}
                        aria-busy={clearingType === type}
                      >
                        {clearingType === type ? (
                          <span className="inline-flex h-4 w-4 items-center justify-center">
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border border-gray-400 border-t-transparent dark:border-gray-500" />
                          </span>
                        ) : (
                          <TrashIcon className="w-4 h-4 text-muted-foreground group-hover:text-red-600 dark:group-hover:text-red-400" />
                        )}
                      </button>
                    </div>

                    {entry.skipped ? (
                      <div className="text-xs text-muted-foreground dark:text-muted-foreground italic">
                        Skipped by user
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-sm text-foreground dark:text-muted-foreground whitespace-pre-wrap">
                          {displayText}
                        </div>
                        {shouldTruncate && (
                          <button
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            onClick={() =>
                              setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }))
                            }
                          >
                            {isExpanded ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border dark:border-gray-700">
                      <span className="text-xs text-muted-foreground dark:text-muted-foreground">
                        {formatDate(entry.updatedAt)}
                      </span>
                      {entry.placeholderCount > 0 && (
                        <span className="text-xs text-muted-foreground dark:text-muted-foreground">
                          {entry.placeholderCount} placeholder{entry.placeholderCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {hasAnyClarifications && (
          <div className="px-6 py-4 border-t border-border dark:border-gray-800">
            <button
              onClick={handleClearAll}
              className={[
                'w-full px-4 py-3 rounded-lg border text-red-700 dark:text-red-300 font-medium text-sm flex items-center justify-center gap-2 transition-colors',
                'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
                clearingAll || clearingType ? 'opacity-70 cursor-not-allowed' : 'hover:bg-red-100 dark:hover:bg-red-900/50'
              ].join(' ')}
              disabled={clearingAll || clearingType !== null}
              aria-busy={clearingAll}
            >
              {clearingAll ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border border-red-400 border-t-transparent dark:border-red-300" />
                  Clearing...
                </>
              ) : (
                <>
                  <TrashIcon className="w-4 h-4" />
                  Clear All Clarifications
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
